(async () => {
    const { Core } = await importModule('Core');
    const { $replaceContent } = await importModule('Utils');
    customElements.define('predictive-search', class extends Core {
        elements = {
            form: 'form', input: 'input[name="q"]', results: '[data-results]',
            wrapper: '[data-wrapper]', status: '[data-status]', close: '[data-search-close]'
        };
        cache = new Map();
        sequence = 0;
        render() {
            this.listeners?.abort();
            this.listeners = new AbortController();
            const options = { signal: this.listeners.signal };
            this.$input.setAttribute('autocomplete', 'off');
            this.$input.setAttribute('role', 'combobox');
            this.$input.setAttribute('aria-autocomplete', 'list');
            this.$input.setAttribute('aria-haspopup', 'dialog');
            this.$input.setAttribute('aria-controls', this.$wrapper.id);
            this.close();
            this.$input.addEventListener('input', () => {
                this.cancel();
                this.close();
                const sequence = this.sequence;
                this.timer = setTimeout(() => this.search(sequence), 250);
            }, options);
            this.$input.addEventListener('focus', () => {
                if (this.cache.has(this.query)) this.show(this.cache.get(this.query));
            }, options);
            this.$close.addEventListener('click', () => {
                this.$input.focus();
                this.cancel();
                this.close();
            }, options);
            this.addEventListener('keydown', event => {
                if (event.key === 'Escape') {
                    event.preventDefault();
                    this.$input.focus();
                    this.cancel();
                    this.close();
                } else if (event.key === 'ArrowDown' && !this.$wrapper.hidden) {
                    const links = Array.from(this.$results.querySelectorAll('a[href]'));
                    const index = links.indexOf(document.activeElement);
                    if (links.length) {
                        event.preventDefault();
                        links[Math.min(index + 1, links.length - 1)].focus();
                    }
                } else if (event.key === 'ArrowUp' && !this.$wrapper.hidden) {
                    const links = Array.from(this.$results.querySelectorAll('a[href]'));
                    const index = links.indexOf(document.activeElement);
                    event.preventDefault();
                    (index > 0 ? links[index - 1] : this.$input).focus();
                }
            }, options);
            this.addEventListener('focusout', event => {
                if (!this.contains(event.relatedTarget)) {
                    this.cancel();
                    this.close();
                }
            }, options);
            document.addEventListener('pointerdown', event => {
                if (!this.contains(event.target)) {
                    this.cancel();
                    this.close();
                }
            }, options);
            this.$form.addEventListener('submit', () => {
                this.cancel();
                this.close();
            }, options);
        }
        get query() { return this.$input.value.trim(); }
        cancel() {
            clearTimeout(this.timer);
            this.request?.abort();
            this.sequence++;
            this.classList.remove('!loading');
            this.$results.setAttribute('aria-busy', 'false');
        }
        close() {
            this.$wrapper.hidden = true;
            this.$wrapper.classList.remove('!active');
            this.$input.setAttribute('aria-expanded', 'false');
            this.$status.textContent = '';
        }
        async search(sequence) {
            const query = this.query;
            if (query.length < 2 || sequence !== this.sequence) return;
            if (this.cache.has(query)) {
                this.show(this.cache.get(query));
                return;
            }
            this.request = new AbortController();
            this.classList.add('!loading');
            this.$results.setAttribute('aria-busy', 'true');
            const params = new URLSearchParams({
                q: query,
                'resources[type]': this.getAttribute('sources') || 'product,collection',
                'resources[limit]': this.getAttribute('results-limit') || '6',
                'resources[limit_scope]': 'all',
                'resources[options][unavailable_products]': this.hasAttribute('only-stock-products') ? 'hide' : 'last',
                'resources[options][fields]': 'title,product_type,variants.title,vendor,variants.sku,variants.barcode',
                section_id: 'r_predictive-search'
            });
            try {
                const response = await fetch(routes.predictive_search_url + '?' + params, { signal: this.request.signal });
                if (!response.ok) throw new Error('Search unavailable');
                const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
                const template = doc.querySelector('[data-template]');
                if (!template) throw new Error('Search response unavailable');
                if (sequence !== this.sequence || query !== this.query) return;
                if (this.cache.size >= 30) this.cache.delete(this.cache.keys().next().value);
                this.cache.set(query, template);
                this.show(template);
            } catch (error) {
                if (error.name !== 'AbortError' && sequence === this.sequence) this.close();
            } finally {
                if (sequence === this.sequence) {
                    this.classList.remove('!loading');
                    this.$results.setAttribute('aria-busy', 'false');
                }
            }
        }
        show(template) {
            if (!this.contains(document.activeElement)) return;
            this.$status.textContent = template.dataset.announcement || '';
            if (template.hasAttribute('data-empty-results')) {
                this.$wrapper.hidden = true;
                this.$input.setAttribute('aria-expanded', 'false');
                return;
            }
            $replaceContent(this.$results, template);
            this.$results.hidden = false;
            this.$wrapper.hidden = false;
            this.$wrapper.classList.add('!active');
            this.$input.setAttribute('aria-expanded', 'true');
        }
        disconnect() {
            this.cancel();
            this.listeners?.abort();
        }
    });
})();
